import { useVersionCheck } from "@/hooks/use-version-check";
import { RefreshCw } from "lucide-react";
import { BottomToast, BOTTOM_TOAST_TAP } from "@/components/BottomToast";

/**
 * #769 — stale-bundle self-refresh surface.
 *
 * Mounted once in the app shell (so the version check runs on every in-app
 * page). It drives `useVersionCheck`, which silently reloads an idle client
 * when a new build is live. When the client is busy (chat streaming / unsaved
 * form input) it instead surfaces this small, non-blocking toast so the user
 * decides when to refresh — an auto-reload must never drop half-typed work.
 *
 * The repo has no toast library (only ui/Skeleton), so this is a minimal,
 * theme-token-styled toast built for this single purpose rather than a bespoke
 * variant of an existing component. Anchored bottom-centre with an iOS
 * safe-area offset so it isn't hidden behind the home indicator in
 * standalone home-screen mode.
 *
 * #5114 TOASTCROSBOT: on a phone that anchor sat on the Ask Crosbot pill. The
 * box, anchor and lift are BottomToast's now, shared with NoticeToast and the
 * Knowledge Base undo bar, and Refresh is a 44 px tap target below lg.
 */
export function UpdateToast({
  unattended = false,
  sessionLapsed = false,
}: { unattended?: boolean; sessionLapsed?: boolean } = {}) {
  // #4832: Display Mode mounts its own (it sits outside the shell) and says it
  // is unattended, so a new build reloads it with nobody there to tap.
  // #4836: and says when its session has lapsed, so it waits for the session
  // instead of reloading into the sign-in page.
  const { updateReady, refreshNow } = useVersionCheck({ unattended, sessionLapsed });

  if (!updateReady) return null;

  return (
    <BottomToast>
      <RefreshCw className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <span className="flex-1 leading-tight">New version available</span>
      <button
        type="button"
        onClick={refreshNow}
        className={`${BOTTOM_TOAST_TAP} shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
      >
        Refresh
      </button>
    </BottomToast>
  );
}
